import { Text } from "@dropbox/dig-components/typography";
import { ChatDiagnosticsViewerNode } from "@mirage/mosaics/ConversationAssistant/containers/ConversationAssistantPageContent/diagnostics/ChatDiagnosticsViewerNode";
import { WorkflowErrorsUI } from "@mirage/mosaics/ConversationAssistant/containers/ConversationAssistantPageContent/diagnostics/WorkflowErrorsUI";
import { WorkflowProcessValuesUI } from "@mirage/mosaics/ConversationAssistant/containers/ConversationAssistantPageContent/diagnostics/WorkflowProcessValuesUI";
import {
  AnswersWorkflowProcessName,
  type AnswersWorkflowProcess,
  type Diagnostics,
} from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/answers_pb";
import type { DocumentInfo } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/models_pb";
import { tagged } from "@mirage/service-logging";
import type { ReactNode } from "react";
import { memo } from "react";

const logger = tagged("ChatDiagnosticsViewerProcess");

interface ChatDiagnosticsViewerProcessProps {
  process: AnswersWorkflowProcess;
  diagnostics: Diagnostics;
  expandedAll?: boolean;
}
export const ChatDiagnosticsViewerProcess = memo(
  ({
    process,
    diagnostics,
    expandedAll,
  }: ChatDiagnosticsViewerProcessProps) => {
    const subheader = getSubheader(process, diagnostics);
    const processName = AnswersWorkflowProcessName[process.name];
    return (
      <ChatDiagnosticsViewerNode
        isError={process.errors.length > 0}
        title={processName}
        subheader={subheader}
        defaultExpanded={expandedAll}
      >
        <WorkflowProcessValuesUI
          process={process}
          values={process.inputs || []}
          namePrefix="[Input]"
        />
        <WorkflowProcessValuesUI
          process={process}
          values={process.outputs || []}
          namePrefix="[Output]"
        />
        <WorkflowErrorsUI
          process={process}
          errors={process?.errors || []}
          namePrefix="[Error]"
        />
        {process.processes.length > 0 && (
          <ChatDiagnosticsViewerNode
            title="Subprocesses"
            defaultExpanded={expandedAll}
          >
            {process.processes.map((process, i) => (
              <ChatDiagnosticsViewerProcess
                key={i}
                process={process}
                diagnostics={diagnostics}
                expandedAll={expandedAll}
              />
            ))}
          </ChatDiagnosticsViewerNode>
        )}
      </ChatDiagnosticsViewerNode>
    );
  },
);
ChatDiagnosticsViewerProcess.displayName = "ChatDiagnosticsViewerProcess";

function getSubheader(
  process: AnswersWorkflowProcess,
  diagnostics: Diagnostics,
): ReactNode {
  const timingString = getTimingString(process, diagnostics);

  if (process.name === AnswersWorkflowProcessName.DOWNLOAD_DOCUMENTS_CONTENT) {
    const docNames =
      process.inputs
        ?.filter(
          (input) =>
            input.name === "search_results" && input.value.case === "valueStr",
        )
        ?.map((input) => input.value.value) || [];

    return (
      <>
        {timingString}
        {(docNames || []).length > 0 && (
          <>
            <Text isBold size="xsmall" tagName="div">
              Titles: {docNames.join(", ")}
            </Text>
          </>
        )}
      </>
    );
  } else if (process.name === AnswersWorkflowProcessName.CONTEXT_SELECTION) {
    const configStr = process.inputs.find(
      (input) =>
        input.name === "context_selection_config" &&
        input.value.case === "valueJsonStr",
    )?.value.value as string;
    try {
      const config = JSON.parse(configStr);
      const selectedDocs = (config?.documentInfos || []) as DocumentInfo[];
      const selectedDocNames = selectedDocs.map((doc) => doc.title);
      return (
        <>
          {timingString}
          <Text isBold size="xsmall" tagName="div">
            Titles: {selectedDocNames.join(", ")}
          </Text>
        </>
      );
    } catch (e) {
      logger.error(
        "Context selection process config is not a valid JSON string",
        e,
      );
    }
  } else if (process.name === AnswersWorkflowProcessName.LLM_INTERACTION) {
    const docNames =
      process.inputs
        ?.filter(
          (input) =>
            input.name === "documents" && input.value.case === "valueStr",
        )
        ?.map((input) => input.value.value) || [];
    return (
      <>
        {timingString}
        {(docNames || []).length > 0 && (
          <>
            <Text isBold size="xsmall" tagName="div">
              Titles: {docNames.join(", ")}
            </Text>
          </>
        )}
      </>
    );
  }

  return timingString;
}

function getTimingString(
  process: AnswersWorkflowProcess,
  diagnostics: Diagnostics,
): string {
  const timeTaken = getTimeTakenInMillis(process);

  const executionStart = durationBetweenTimestamps(
    diagnostics.executionStartTimestamp,
    process.executionStartTimestamp,
  );

  const executionEnd = durationBetweenTimestamps(
    diagnostics.executionStartTimestamp,
    process.executionEndTimestamp,
  );

  return `Time taken: ${timeTaken}. Execution started at: ${executionStart}, and ended at: ${executionEnd}.`;
}

const getTimeTakenInMillis = (
  process?: AnswersWorkflowProcess | Diagnostics,
): string => {
  if (
    !process ||
    !process.executionStartTimestamp ||
    !process.executionEndTimestamp
  ) {
    return "N/A";
  }

  const startTimeStamp = Number(process.executionStartTimestamp || 0);
  const endTimeStamp = Number(process.executionEndTimestamp || 0);

  const milliseconds: number = endTimeStamp - startTimeStamp;

  return `${Math.floor(milliseconds)}ms`;
};

const durationBetweenTimestamps = (start?: string, end?: string): string => {
  if (!start || !end) {
    return "N/A";
  }
  const startTime = new Date(parseInt(start));
  const endTime = new Date(parseInt(end));
  const time = (endTime.getTime() - startTime.getTime()) / 1000;
  return `${Math.floor(time * 100) / 100}s`;
};
